
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Globe } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";

type LanguageOption = {
  value: string;
  label: string;
  nativeName: string;
};

const languages: LanguageOption[] = [
  { value: "en", label: "English", nativeName: "English" },
  { value: "es", label: "Spanish", nativeName: "Español" },
  { value: "fr", label: "French", nativeName: "Français" },
  { value: "de", label: "German", nativeName: "Deutsch" },
  { value: "zh", label: "Chinese", nativeName: "中文" },
  { value: "ja", label: "Japanese", nativeName: "日本語" },
  { value: "ko", label: "Korean", nativeName: "한국어" },
  { value: "pt", label: "Portuguese", nativeName: "Português" },
];

interface LanguageTabProps {
  isMobile: boolean;
}

export function LanguageTab({ isMobile }: LanguageTabProps) {
  const { t } = useTranslation();
  const [language, setLanguage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Get the current language from localStorage or use browser language as fallback
    const currentLanguage = localStorage.getItem("appLanguage") || navigator.language.split("-")[0];
    setLanguage(currentLanguage);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Save language preference to localStorage
      localStorage.setItem("appLanguage", language);
      
      // Apply language change to the document
      document.documentElement.lang = language;
      
      // Dispatch a custom event to notify other components about the language change
      window.dispatchEvent(new CustomEvent('languageChange', { detail: { language } }));
      
      console.log("Language updated to:", language);
      
      // Show success toast
      toast.success(`${t('language_changed')} ${languages.find(lang => lang.value === language)?.label || language}`);
      
      // Simulate delay for feedback
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error("Failed to update language:", error);
      toast.error(t('failed_update_language'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border bg-card">
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2">
          <Globe className="h-5 w-5" />
          {t('language')}
        </CardTitle>
        <CardDescription>
          {t('language_description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <RadioGroup
            value={language}
            onValueChange={setLanguage}
            className="grid gap-4 grid-cols-1 md:grid-cols-2"
          >
            {languages.map((lang) => (
              <div
                key={lang.value}
                className="flex items-center space-x-2 border rounded-md p-3 hover:bg-accent"
              >
                <RadioGroupItem value={lang.value} id={`lang-${lang.value}`} />
                <Label htmlFor={`lang-${lang.value}`} className="flex-1 cursor-pointer">
                  <div className="flex justify-between">
                    <span>{lang.label}</span>
                    <span className="text-muted-foreground">{lang.nativeName}</span>
                  </div>
                </Label>
              </div>
            ))}
          </RadioGroup>
          
          <Button 
            type="submit" 
            className="mt-6 w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting ? t('updating') : t('save_language')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
